import { useState, useEffect } from 'react';

import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, CreditCard, ArrowRight } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { billingApi, PaymentMethod } from '@/lib/api/billing';
import { PlanType } from '@optidev/shared-types';
import toast from 'react-hot-toast';

interface UpgradeConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  planType: PlanType.STANDARD | PlanType.PRO;
  interval: 'month' | 'year';
}

interface UpgradePreview {
  currentPlan: string;
  newPlan: string;
  newInterval: string;
  newPricePerInterval: number;
  prorationAmount: number;
  tax: number;
  totalDueNow: number;
  nextBillingDate: string;
  nextBillingAmount: number;
}

interface UpgradeResult {
  newPlan: string;
  newInterval: string;
  subtotal: number;
  tax: number;
  amountCharged: number;
  cardLabel: string;
}

const formatCents = (cents: number): string => (cents / 100).toFixed(2);

const formatBrand = (brand: string): string => {
  const brands: Record<string, string> = {
    visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex',
    discover: 'Discover', diners: 'Diners', jcb: 'JCB', unionpay: 'UnionPay',
  };
  return brands[brand] || brand.charAt(0).toUpperCase() + brand.slice(1);
};

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export function UpgradeConfirmModal({
  isOpen,
  onClose,
  onSuccess,
  planType,
  interval,
}: UpgradeConfirmModalProps) {
  const { getIdToken } = useAuth();
  const [previewLoading, setPreviewLoading] = useState(true);
  const [preview, setPreview] = useState<UpgradePreview | null>(null);
  const [card, setCard] = useState<PaymentMethod | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<UpgradeResult | null>(null);

  useEffect(() => {
    if (isOpen && !result) {
      loadPreview();
    }
    if (!isOpen) {
      setResult(null);
    }
  }, [isOpen, planType, interval]);

  const loadPreview = async (): Promise<void> => {
    setPreviewLoading(true);
    setPreview(null);
    setCard(null);
    try {
      const token = await getIdToken();
      if (!token) return;

      const [previewData, pmResponse] = await Promise.all([
        billingApi.previewUpgrade(token, planType, interval),
        billingApi.getPaymentMethods(token).catch(() => null),
      ]);

      setPreview(previewData);

      if (pmResponse?.paymentMethods?.length) {
        const subscriptionDefault = pmResponse.paymentMethods.find(pm => pm.isSubscriptionDefault);
        const invoiceDefault = pmResponse.paymentMethods.find(pm => pm.isInvoiceDefault);
        setCard(subscriptionDefault || invoiceDefault || pmResponse.paymentMethods[0]);
      }
    } catch (error: unknown) {
      const axiosErr = error as { response?: { data?: { message?: string } } };
      toast.error(axiosErr.response?.data?.message || 'Failed to load upgrade details');
      onClose();
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleUpgrade = async (): Promise<void> => {
    setLoading(true);
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const response = await billingApi.upgradeSubscription(token, planType, interval);

      setResult({
        newPlan: planType,
        newInterval: interval,
        subtotal: response.subtotal,
        tax: response.tax,
        amountCharged: response.amountCharged,
        cardLabel: card ? `${formatBrand(card.brand)} •••• ${card.last4}` : '',
      });
    } catch (error: unknown) {
      const axiosErr = error as { response?: { data?: { message?: string } } };
      toast.error(axiosErr.response?.data?.message || 'Upgrade failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDone = (): void => {
    setResult(null);
    onSuccess();
    onClose();
  };

  const cardLabel = card
    ? `${formatBrand(card.brand)} •••• ${card.last4}`
    : '';

  return (
    <Dialog open={isOpen} onOpenChange={result ? handleDone : onClose}>
      <DialogContent className="max-w-[90vw] w-full sm:max-w-[440px] rounded-xl p-0 gap-0" onOpenAutoFocus={(e) => e.preventDefault()}>

        {/* Success state */}
        {result ? (
          <div className="px-6 py-8">
            <div className="text-center mb-6">
              <p className="text-sm font-medium text-muted-foreground">Payment Confirmed</p>
              <p className="text-xl font-semibold mt-1">
                Upgraded to {capitalize(result.newPlan)}
              </p>
              <p className="text-sm text-muted-foreground mt-0.5">
                {result.newInterval === 'year' ? 'Annual' : 'Monthly'} plan
              </p>
            </div>

            <div className="border-t border-b border-border py-4 space-y-2.5">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="tabular-nums">${formatCents(result.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tax</span>
                <span className="tabular-nums">${formatCents(result.tax)}</span>
              </div>
              <div className="flex justify-between text-sm font-medium">
                <span>Charged</span>
                <span className="tabular-nums">${formatCents(result.amountCharged)}</span>
              </div>
            </div>

            {result.cardLabel && (
              <div className="flex items-center gap-2 mt-4 mb-6">
                <CreditCard className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{result.cardLabel}</span>
              </div>
            )}

            <Button
              onClick={handleDone}
              className="w-full h-12 text-sm font-medium bg-foreground hover:bg-foreground/90 text-background"
            >
              Done
            </Button>
          </div>
        ) : (
          <div className="px-6 py-6">
            {previewLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : preview ? (
              <>
                {/* Header */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold">
                    Upgrade to {capitalize(preview.newPlan)} {preview.newInterval === 'year' ? 'Annual' : 'Monthly'}
                  </h3>
                </div>

                {/* Price breakdown */}
                <div className="border-t border-b border-border py-4 space-y-2.5 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {capitalize(preview.newPlan)} ({preview.newInterval === 'year' ? 'Annual' : 'Monthly'})
                    </span>
                    <span className="tabular-nums">${formatCents(preview.newPricePerInterval)}/{preview.newInterval === 'year' ? 'yr' : 'mo'}</span>
                  </div>
                  {preview.prorationAmount !== preview.totalDueNow - preview.tax && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Prorated for remaining days</span>
                      <span className="tabular-nums">${formatCents(preview.prorationAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax</span>
                    <span className="tabular-nums">${formatCents(preview.tax)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium pt-2 border-t border-border">
                    <span>Due now</span>
                    <span className="tabular-nums">${formatCents(preview.totalDueNow)}</span>
                  </div>
                </div>

                {/* Card info */}
                {card && (
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {cardLabel} · {String(card.expMonth).padStart(2, '0')}/{String(card.expYear).slice(-2)}
                      </span>
                    </div>
                    <button
                      onClick={async () => {
                        const token = await getIdToken();
                        if (!token) return;
                        try {
                          const { url } = await billingApi.createPortalSession(token);
                          window.open(url, '_blank');
                        } catch {
                          toast.error('Failed to open billing portal');
                        }
                      }}
                      className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                    >
                      Change
                    </button>
                  </div>
                )}

                {/* CTA */}
                <Button
                  onClick={handleUpgrade}
                  disabled={loading}
                  className="w-full h-12 text-sm font-medium bg-foreground hover:bg-foreground/90 text-background"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    `Upgrade — $${formatCents(preview.totalDueNow)}`
                  )}
                </Button>

                {/* Fine print */}
                <p className="text-[11px] text-muted-foreground/70 text-center mt-3">
                  Next billing: ${formatCents(preview.nextBillingAmount)}/{preview.newInterval === 'year' ? 'yr' : 'mo'} + tax on {new Date(preview.nextBillingDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </>
            ) : (
              <div className="py-12 text-center text-sm text-muted-foreground">
                Unable to load upgrade details. Please try again.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
